/**
 * Email validation that MIRRORS THE DATABASE, character for character.
 *
 * WHY THIS EXISTS
 * `companies.email` and `customers.email` both carry a CHECK constraint:
 *
 *   CHECK (email IS NULL OR email ~* '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$')
 *
 * Signup validated only that the field was non-empty, so `notreal@notld` passed the
 * form, reached Postgres, and came back as a raw constraint violation shown verbatim to
 * the user in a red box:
 *
 *   new row for relation "companies" violates check constraint "chk_companies_email_format"
 *
 * Two failures in one: no client check, and a database internal leaked to a person
 * signing up.
 *
 * THE REGEX IS COPIED FROM THE CONSTRAINT ON PURPOSE. A "good enough" pattern is worse
 * than none here, because any mismatch reopens exactly this bug from the other side:
 * looser than the DB and the constraint error comes back anyway; stricter and the form
 * rejects an address the database would have accepted. The existing ad-hoc pattern in
 * CompanyProfileSettingsTab (`^[^\s@]+@[^\s@]+\.[^\s@]+$`) is the looser kind — it
 * accepts a one-character TLD and characters the column rejects — so that surface can
 * still produce the raw error. Left alone here rather than swept: this file is the
 * canonical answer, and repointing that caller is a separate, verifiable change.
 *
 * Keep in step with the constraint. If the CHECK is ever altered, change this with it —
 * they are one rule in two places, and only the database's copy is enforced.
 */

// Verified against pg_constraint on 2026-09-10 (chk_companies_email_format and
// chk_customers_email_format are identical).
const DB_EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** True when the database would accept this address. Empty/blank is NOT valid here —
 *  "is it required" is the caller's question, this only answers "is it well-formed". */
export const isValidEmail = (value) => DB_EMAIL_PATTERN.test(String(value ?? '').trim());

/**
 * Turn a Postgres error into something a person can act on.
 *
 * Server messages reach the UI verbatim today. A constraint name tells the user nothing,
 * tells an attacker a little, and hides the one thing they needed to know — which field
 * is wrong and why.
 */
export const friendlyDbError = (message) => {
  const raw = String(message ?? '');
  if (!raw) return null;

  if (/chk_(companies|customers)_email_format/i.test(raw)) {
    return 'That email address is not valid. Check for a missing domain ending, like .com.';
  }
  // Any other CHECK violation: say which rule failed without printing its internal name.
  if (/violates check constraint/i.test(raw)) {
    return 'One of the details entered is not in a valid format. Please review and try again.';
  }
  if (/duplicate key|already exists|unique constraint/i.test(raw)) {
    return 'That value is already in use.';
  }
  if (/violates not-null constraint/i.test(raw)) {
    return 'A required detail is missing. Please complete every required field.';
  }
  if (/violates foreign key constraint/i.test(raw)) {
    return 'Something this record depends on no longer exists. Refresh and try again.';
  }
  return null; // Not a recognised database error — let the caller show its own message.
};
